import React, { Fragment, useState, useMemo } from 'react';
import compose from 'lodash.flowright';
import moment from 'moment';
import PropTypes from 'prop-types';
import qs from 'query-string';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { graphql } from 'react-apollo';

import { mapProps } from '../../../../../../util/hocs';
import AppStoreLink from '../../../../../../components/global/AppStoreLink';
import Button from '../../../../../../components/global/Button';
import HierarchyItemCard from './components/HierarchyItemCard';
import getConfig from '../../../../../../util/config';
import Logo from '../../../../../../components/global/Logo';
import PlayStoreLink from '../../../../../../components/global/PlayStoreLink';
import { getGalleriesFromHierarchyItems } from './helpers';
import { HIERARCHY_ITEM_TYPE } from '../../../../../../constants/albums';
import { isHumanJoinCode } from '../../../../../../util/join-code';
import { openModal as openModalAction } from '../../../../../../actions/modal-actions';
import { USERPAGE_PRO_ONBOARDING_MODAL } from '../../../../../../constants/modals';
import { ProOnboardingCTAModal } from './components/ProOnboardingCTA';
import {
  ACCOUNT_PROFILE_HIERARCHY_ITEMS_SORT,
  getAccountProfileName,
} from '../../helpers';
import { SORT_DIRECTION } from '../../../../../../constants/sort';
import { APOLLO_NETWORK_STATUS } from '../../../../../../constants/apollo-network-status';
import { createLoadMoreMethod } from '../../../../../../util/apollo';
import FullScreenLoader from '../../../../../../components/global/FullScreenLoader';
import { proAccountProfile } from '../../graphql';
import { getProDirectoryFormUrl } from '../../../AlbumSearch/helpers';
import ProfilePageHelmet from './components/ProfilePageHelmet/ProfilePageHelmet';
import ProUserPageNullState from './components/ProUserPageNullState/ProUserPageNullState';
import ProfilePageHero from '../ProfilePageHero/ProfilePageHero';
import { ALL_PHOTOS_MODE } from '../../../../../../constants/album-folder';

const GALLERY_TARGETS = {
  REDIRECT_TO_WEB_GALLERY: 'REDIRECT_TO_WEB_GALLERY',
  REDIRECT_TO_ONBOARDING: 'REDIRECT_TO_ONBOARDING',
  ONBOARDING_MODAL_LOCKED: 'ONBOARDING_MODAL_LOCKED',
  ONBOARDING_MODAL_UNLOCKED: 'ONBOARDING_MODAL_UNLOCKED',
};

const propTypes = {
  accountProfile: PropTypes.shape({
    hierarchy_items: PropTypes.shape({
      records: PropTypes.arrayOf(
        PropTypes.shape({
          item_type: PropTypes.string,
        }),
      ),
    }),
    logo_image: PropTypes.shape({
      url: PropTypes.string,
    }),
    name: PropTypes.string,
    organization_name: PropTypes.string,
  }).isRequired,
  loading: PropTypes.bool,
  loadingMore: PropTypes.bool,
  loadMore: PropTypes.func,
  futureLoadMore: PropTypes.func,
  location: PropTypes.shape({
    search: PropTypes.string,
  }).isRequired,
  openModal: PropTypes.func,
  onGoBack: PropTypes.func,
};

const ProUserPage = ({
  accountProfile,
  loading,
  loadingMore,
  loadMore,
  futureLoadMore,
  location,
  openModal,
  onGoBack,
}) => {
  const config = getConfig();
  const [itemInFocus, setItemInFocus] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchVisible, setSearchVisible] = useState(false);

  const {
    past_hierarchy_items: {
      records: pastHierarchyItemRecords = [],
      total: pastHierarchyItemRecordsTotal = 0,
    } = {},
    future_hierarchy_items: {
      records: futureHierarchyItemRecords = [],
      total: futureHierarchyItemRecordsTotal = 0,
    } = {},
    organization_name: organizationName = '',
    site_url: siteUrl = '',
    pro_directory: proDirectory,
  } = accountProfile ?? {};

  const futureGalleries = getGalleriesFromHierarchyItems(
    accountProfile?.future_hierarchy_items?.records ?? [],
  );
  const pastGalleries = getGalleriesFromHierarchyItems(
    accountProfile?.past_hierarchy_items?.records ?? [],
  );

  // Mini hack: Sort alphabetically for presspass user
  // Remove this after correct support has been added.
  const isPresspassUser =
    (accountProfile?.username ?? '').toLowerCase() === 'presspass';

  const sortedFutureGalleries = useMemo(() => {
    if (!isPresspassUser || futureGalleries.length === 0) {
      return futureGalleries;
    }

    return futureGalleries.sort((a, b) => {
      const nameA = (a.album?.name || a.album_folder?.name || '').toLowerCase();
      const nameB = (b.album?.name || b.album_folder?.name || '').toLowerCase();
      return nameA.localeCompare(nameB);
    });
  }, [isPresspassUser, futureGalleries]);

  const sortedPastGalleries = useMemo(() => {
    if (!isPresspassUser || pastGalleries.length === 0) {
      return pastGalleries;
    }

    return pastGalleries.sort((a, b) => {
      const nameA = (a.album?.name || a.album_folder?.name || '').toLowerCase();
      const nameB = (b.album?.name || b.album_folder?.name || '').toLowerCase();
      return nameA.localeCompare(nameB);
    });
  }, [isPresspassUser, pastGalleries]);

  const filteredFutureGalleries = useMemo(() => {
    if (!searchQuery) {
      return sortedFutureGalleries;
    }

    const lowerQuery = searchQuery.toLowerCase();
    return sortedFutureGalleries.filter(
      hierarchyItem =>
        hierarchyItem?.album?.name?.toLowerCase().includes(lowerQuery) ||
        hierarchyItem?.album_folder?.name?.toLowerCase().includes(lowerQuery),
    );
  }, [sortedFutureGalleries, searchQuery]);

  const filteredPastGalleries = useMemo(() => {
    if (!searchQuery) {
      return sortedPastGalleries;
    }

    const lowerQuery = searchQuery.toLowerCase();
    return sortedPastGalleries.filter(
      hierarchyItem =>
        hierarchyItem?.album?.name?.toLowerCase().includes(lowerQuery) ||
        hierarchyItem?.album_folder?.name?.toLowerCase().includes(lowerQuery),
    );
  }, [sortedPastGalleries, searchQuery]);

  const hasGalleries =
    sortedPastGalleries.length > 0 || sortedFutureGalleries.length > 0;

  // If url contains a set `p` param.
  const hasUnblockedOnboardingModal = !!qs.parse(location.search)?.p ?? false;

  const displayLoadMore =
    pastHierarchyItemRecords.length < pastHierarchyItemRecordsTotal &&
    pastHierarchyItemRecords.length > 0;

  const displayFutureLoadMore =
    futureHierarchyItemRecords.length < futureHierarchyItemRecordsTotal &&
    futureHierarchyItemRecords.length > 0;

  const determineHierarchyItemTarget = ({
    is_web_gallery: hasWebGallery,
    join_code: joinCode,
    has_password: hasPassword,
    joins_enabled: joinsEnabled,
    all_photos_mode: allPhotosMode,
    root_album_folder: root,
  }) => {
    // Determine what should happen when a gallery is clicked.
    const hasJoinCode = isHumanJoinCode(joinCode) || !!root?.join_code;

    const {
      REDIRECT_TO_WEB_GALLERY,
      ONBOARDING_MODAL_LOCKED,
      ONBOARDING_MODAL_UNLOCKED,
      REDIRECT_TO_ONBOARDING,
    } = GALLERY_TARGETS;

    switch (true) {
      // 1 Web gallery enabled, but no valid human readable join code and non-joinable.
      case hasWebGallery && !joinsEnabled && !hasJoinCode:
        return REDIRECT_TO_WEB_GALLERY;
      // 2 Has join code. May require password / join code input..
      case (hasJoinCode || hasPassword) && !hasUnblockedOnboardingModal:
        return ONBOARDING_MODAL_LOCKED;
      // 3.1 R/L Door situation.
      case hasWebGallery && joinsEnabled && !hasUnblockedOnboardingModal:
        return ONBOARDING_MODAL_LOCKED;
      // 3.2 R/L Door situation without block.
      case hasWebGallery && joinsEnabled && hasUnblockedOnboardingModal:
        return ONBOARDING_MODAL_UNLOCKED;
      // 3.3 Public all photos mode with joins enabled.
      case allPhotosMode === ALL_PHOTOS_MODE.PUBLIC && joinsEnabled:
        return ONBOARDING_MODAL_UNLOCKED;
      // 4 No web gallery, no join code but joins enabled
      case !hasWebGallery && joinsEnabled && !hasJoinCode:
        return REDIRECT_TO_ONBOARDING;
      // 5 No web gallery, but valid human readable join code although with joins enabled.
      // May require password / join code input.
      case !hasWebGallery &&
        joinsEnabled &&
        hasJoinCode &&
        !hasUnblockedOnboardingModal:
        return ONBOARDING_MODAL_LOCKED;
      // 6 No web gallery, but valid human readable join code, and overloaded unlocked modal content.
      case !hasWebGallery && joinsEnabled && hasUnblockedOnboardingModal:
        return REDIRECT_TO_ONBOARDING;
      // todo: revise, unreachable at present // 6 Neither web gallery or valid human readable join code.
      case !hasWebGallery && !joinsEnabled && !hasJoinCode:
      default:
        return 'NONE';
    }
  };

  const handleGalleryClick = hierarchyItem => {
    // Redirect to a web gallery, or open the modal which will be locked or unlocked (determined here, handled in modal).
    const { type: hierarchyItemType } = hierarchyItem;
    const interactionState = determineHierarchyItemTarget(
      hierarchyItemType === HIERARCHY_ITEM_TYPE.ALBUM_FOLDER
        ? hierarchyItem.album_folder
        : hierarchyItem.album,
    );
    const {
      REDIRECT_TO_WEB_GALLERY,
      REDIRECT_TO_ONBOARDING,
      ONBOARDING_MODAL_LOCKED,
      ONBOARDING_MODAL_UNLOCKED,
    } = GALLERY_TARGETS;

    switch (interactionState) {
      case REDIRECT_TO_WEB_GALLERY:
      default:
        window.location.assign(
          `${getConfig().app.baseUrl}/galleries/view/${
            hierarchyItemType === HIERARCHY_ITEM_TYPE.ALBUM_FOLDER
              ? 'folder'
              : 'album'
          }/${
            hierarchyItemType === HIERARCHY_ITEM_TYPE.ALBUM_FOLDER
              ? hierarchyItem.album_folder.id
              : hierarchyItem.album.id
          }`,
        );
        break;
      case REDIRECT_TO_ONBOARDING: {
        const id =
          hierarchyItem.album_folder?.id ??
          hierarchyItem.album?.root_album_folder?.id;
        window.location.assign(
          `${getConfig().app.baseUrl}/discover/album-folder/${id}`,
        );
        break;
      }
      case ONBOARDING_MODAL_LOCKED:
      case ONBOARDING_MODAL_UNLOCKED:
        setItemInFocus(hierarchyItem);
        openModal(USERPAGE_PRO_ONBOARDING_MODAL, {
          // If available, skips directly to WG
          requireUserInteraction: false,
        });
        break;
    }
  };

  const handleUpload = hierarchyItem => {
    window.location.assign(
      `${getConfig().app.baseUrl}/contributors/u/${
        hierarchyItem.album_folder?.id ?? hierarchyItem?.album?.id
      }`,
    );
  };

  const handleCloseModal = () => {
    setItemInFocus(null);
  };

  if (loading) {
    return <FullScreenLoader />;
  }

  if (!hasGalleries) {
    // render null state:
    return (
      <ProUserPageNullState
        accountProfile={accountProfile}
        onGoBack={onGoBack}
      />
    );
  }

  // todo: support dynamic header/description content.
  return (
    <Fragment>
      <ProfilePageHelmet accountProfile={accountProfile} />
      <div className="generic-user-page">
        <ProfilePageHero
          accountProfile={accountProfile}
          onGoBack={onGoBack}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          searchVisible={searchVisible}
          setSearchVisible={setSearchVisible}
          hasGalleries={hasGalleries}
        />
        {sortedFutureGalleries.length > 0 && (
          <div className="more-galleries">
            <div className="d-flex justify-content-between align-items-center mb-3 px-2 px-md-5">
              <h4 className="gallery-section-title mb-0">
                RECENT &amp; UPCOMING EVENTS
              </h4>
            </div>
            {!filteredFutureGalleries?.length && !!searchQuery && (
              <div className="text-center">
                No results found for &quot;{searchQuery}&quot;.
              </div>
            )}
            <div className="gallery-listing-grid">
              {filteredFutureGalleries.map(hierarchyItem => (
                <HierarchyItemCard
                  hierarchyItem={hierarchyItem}
                  key={hierarchyItem.id}
                  onItemClick={() => handleGalleryClick(hierarchyItem)}
                  onUpload={() => handleUpload(hierarchyItem)}
                />
              ))}
            </div>

            {displayFutureLoadMore && (
              <Button
                className="load-more-button"
                disabled={loadingMore}
                onClick={() => {
                  setSearchQuery('');
                  futureLoadMore();
                }}
                type="button"
              >
                {loadingMore ? 'Loading...' : 'Load more'}
              </Button>
            )}
          </div>
        )}
        {sortedPastGalleries.length > 0 && (
          <div className="more-galleries">
            <div className="d-flex justify-content-between align-items-center mb-3 px-2 px-md-5">
              <h4 className="gallery-section-title mb-0">PAST EVENTS</h4>
            </div>
            {!filteredPastGalleries?.length && !!searchQuery && (
              <div className="text-center">
                No results found for &quot;{searchQuery}&quot;.
              </div>
            )}
            <div className="gallery-listing-grid">
              {filteredPastGalleries.map(hierarchyItem => (
                <HierarchyItemCard
                  hierarchyItem={hierarchyItem}
                  key={hierarchyItem.id}
                  onItemClick={() => handleGalleryClick(hierarchyItem)}
                  onUpload={() => handleUpload(hierarchyItem)}
                />
              ))}
            </div>

            {displayLoadMore && (
              <Button
                className="load-more-button"
                disabled={loadingMore}
                onClick={() => {
                  setSearchQuery('');
                  loadMore();
                }}
                type="button"
              >
                {loadingMore ? 'Loading...' : 'Load more'}
              </Button>
            )}
          </div>
        )}
        {proDirectory?.is_enabled && (
          <div className="pro-directory-section">
            <p className="text-primary text-uppercase text-extra-bold text-sm">
              About
            </p>
            <h4 className="text-extra-bold">
              {getAccountProfileName(accountProfile)}
            </h4>
            {proDirectory?.description && (
              <p className="mb-2">{proDirectory.description}</p>
            )}
            <div className="pro-directory-button-container">
              {siteUrl && (
                <Button
                  target="_blank"
                  href={siteUrl}
                  btnStyle="primary"
                  btnOutline
                  className="d-flex align-items-center"
                >
                  Visit Website
                </Button>
              )}
              <Button
                href={getProDirectoryFormUrl(accountProfile)}
                target="_blank"
              >
                Contact {organizationName}
              </Button>
            </div>
          </div>
        )}

        <div className="links">
          <div>
            {/* Reason for disable: "security risk" or not we need this link to be followed from searchbots for SEO purposes. */}
            {}
            <a
              href={config?.app?.baseUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Logo imageProps={{ height: '28px' }} />
            </a>

            <p className="slogan">Live the moment.</p>

            <AppStoreLink height={36} />
            <PlayStoreLink height={36} />

            <p className="mt-2">
              &#169; {moment().format('YYYY')} {}
              <a href={config?.app?.baseUrl} target="_blank" rel="noreferrer">
                Waldo Photos
              </a>{' '}
              |{' '}
              <a
                href="/terms#privacy"
                target="_blank"
                rel="noreferrer noopener"
              >
                Privacy Policy
              </a>
            </p>
          </div>
        </div>
        <ProOnboardingCTAModal
          hierarchyItem={itemInFocus}
          onClose={handleCloseModal}
        />
      </div>
    </Fragment>
  );
};

ProUserPage.propTypes = propTypes;

const mapDispatchToProps = {
  openModal: openModalAction,
};

export { ProUserPage as ProUserPageComponent };

const withProAccountProfile = graphql(proAccountProfile, {
  options: ({ match }) => ({
    notifyOnNetworkStatusChange: true,
    variables: {
      username: match.params.username,
      sort: {
        field: ACCOUNT_PROFILE_HIERARCHY_ITEMS_SORT.START_DATE,
        direction: SORT_DIRECTION.DESC,
      },
      with_join_code_only: false,
      web_galleries_only: false,
    },
  }),
  skip: ({ match }) => !match.params.username,
});

export default compose(
  withRouter,
  connect(null, mapDispatchToProps),
  withProAccountProfile,
  mapProps(({ data, ...props }) => ({
    ...props,
    accountProfile: data?.accountProfile,
    loading:
      data?.loading && data?.networkStatus === APOLLO_NETWORK_STATUS.LOADING,
    loadingMore: data?.networkStatus === APOLLO_NETWORK_STATUS.FETCH_MORE, // loadMore
    loadMore: createLoadMoreMethod({
      source: data?.accountProfile,
      recordsPath: 'past_hierarchy_items',
      fetchMore: data?.fetchMore,
    }),
    futureLoadMore: createLoadMoreMethod({
      source: data?.accountProfile,
      recordsPath: 'future_hierarchy_items',
      fetchMore: data?.fetchMore,
      limit: 6,
      limitVariableLabel: 'futureLimit',
      offsetVariableLabel: 'futureOffset',
    }),
  })),
)(ProUserPage);
